import React, { Fragment, PropsWithChildren, useImperativeHandle, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import ModalHandler from '@app/component/overlays/modal/ModalHandler';
import Utilities from '@util/Utilities';
import FormButton from '@app/component/form/button/FormButton';

type Props = {
	onClose?: any,
	title?: string
	actions?: React.ReactNode,
	width?: 'small' | 'normal' | 'large' | '2xlarge' | '3xlarge' | 'full'
	ref: any,

}

const Modal: React.FunctionComponent<PropsWithChildren<Props>> = React.forwardRef(({
																																										 children,
																																										 title,
																																										 onClose,
																																										 width = 'normal',
																																										 actions,
																																									 }, ref: React.Ref<ModalHandler>) => {

	const [isActive, setIsActive] = useState<boolean>(false);
	const elementRef = React.useRef<HTMLDivElement>(null);

	const open = () => {
		setIsActive(true);
	};
	const close = () => {
		setIsActive(false);
		if (!!onClose) onClose();
	};

	useImperativeHandle(ref, () => {
		return {
			open: open,
			close: close,
			isOpen: () => isActive,
			getModalHtmlElement: () => elementRef.current,
		};
	});

	return <Transition show={isActive} as={Fragment}>
		<Dialog as="div" className="relative z-10" onClose={close}>
			<TransitionChild
				as={Fragment}
				enter="ease-out duration-300"
				enterFrom="opacity-0"
				enterTo="opacity-100"
				leave="ease-in duration-200"
				leaveFrom="opacity-100"
				leaveTo="opacity-0"
			>
				<div className="fixed inset-0 bg-white-50 bg-opacity-75 transition-opacity backdrop-blur-sm backdrop-filter" />
			</TransitionChild>

			<div className="fixed inset-0 z-10 overflow-y-auto">
				<div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
					<TransitionChild
						as={Fragment}
						enter="ease-out duration-300"
						enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
						enterTo="opacity-100 translate-y-0 sm:scale-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100 translate-y-0 sm:scale-100"
						leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
					>
						<DialogPanel as="div" ref={elementRef}
												 className={Utilities.classNames('relative transform overflow-hidden rounded-lg bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full',
													 width === 'small' ? 'sm:max-w-sm' : '',
													 width === 'normal' ? 'sm:max-w-lg' : '',
													 width === 'large' ? 'sm:max-w-xl' : '',
													 width === '2xlarge' ? 'sm:max-w-2xl' : '',
													 width === '3xlarge' ? 'sm:max-w-3xl' : '',
													 width === 'full' ? 'sm:max-w-full mx-[10%]' : '',
												 )}>
							<div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
								<button
									type="button"
									className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
									onClick={close}
								>
									<span className="sr-only">Close</span>
									<XMarkIcon className="h-6 w-6" aria-hidden="true" />
								</button>
							</div>
							<div className="inner-wrapper px-4 pt-4 pb-3 mt-3 sm:px-6 text-left">
								{!!title &&
									<DialogTitle as="h3" className="text-lg font-semibold leading-6 text-gray-900">
										{title}
									</DialogTitle>}
								<div className="mt-6">
									{children}
								</div>

							</div>
							<div className="bg-gray-50 px-4 py-3 flex flex-col sm:flex-row-reverse sm:px-6 gap-x-2 gap-y-2">
								{actions}
								<FormButton onClick={close}>Close</FormButton>
							</div>
						</DialogPanel>
					</TransitionChild>
				</div>
			</div>
		</Dialog>
	</Transition>;
});

export default Modal;
